import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Email from "./Email";
import Phone from "./Phone";
import Address from "./Address";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";
import {
  compact,
  every,
  get,
  flatten,
  isArray,
  isEmpty,
  omit,
  sortBy,
  uniq,
} from "lodash";
import { diff } from "deep-object-diff";

import COLORS from "../colors";
import { LOCATION_INFO } from "../constants";
import { CONTACT_TYPES } from "./Contact";

export const USER_FIELDS = {
  // Contact info
  name: "",
  type: "",
  company: "",
  companyID: "",
  position: [],
  emails: [],
  phones: [],
  addresses: [],

  // Additional info
  dob: null,
  married: null,
  children: [],
  preferences: {},
  tokens: [],

  // Employee info
  poid: 0,
  pojob: 1,
  roles: [],
  locations: [],
  hired: null,
  drawAmount: null,

  // Commission settings
  commissionType: "sliding", // 'sliding' | 'flat' | 'none'
  commissionFlatRate: null, // Percentage for flat rate commission
  commissionMinMargin: null, // Minimum margin % required for flat rate
  commissionBrackets: null, // Custom sliding scale brackets [{maxMargin, commission}]
  commissionEffectiveDate: null, // Date current commission schedule takes effect
  commissionScheduleHistory: [], // Array of past schedules [{effectiveDate, endDate, type, flatRate, minMargin, brackets, changedBy, changedAt}]

  // Manager share settings (location-based commission from non-team sales)
  // Array of { locationID: string, sharePercent: number }
  // Manager earns sharePercent from completed sales at their location by salespeople NOT on their team
  managerOf: [],

  // Sales goals by year (set by admin)
  // Object keyed by year: { 2024: 1500000, 2025: 2000000 }
  salesGoals: {},

  // Meta info
  created: null,
  createdBy: null,
  disabled: null,
  disabledReason: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(USER_FIELDS);

// NOTE: When adding or removing a role here, also update EMPLOYEE_ROLE_KEYS in
// scripts/changelog.mjs. That CLI mirrors these keys for its changelog
// role-targeting prompt but can't import this module (it pulls in the store,
// Quasar, etc.), so the two lists must be kept in sync manually.
export const USER_ROLES = {
  customer: {
    label: "Customer",
    icon: "customer",
    color: "",
    widgets: [],
  },
  admin: {
    super: true,
    label: "Administrator",
    icon: "admin",
    color: "admin",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "BillsToPay",
      "PendingDates",
      "AwaitingAck",
      "AwaitingBill",
      "AwaitingDownPayment",
      "AwaitingPayment",
      "ScheduledUnpaidCashSales",
      "CashSaleEventsReminder",
      "PurchaserReadyToOrder",
      "PendingQuotes",
      "QuotesDue",
      "AwaitingReview",
      "OpenSalesLeads",
      "ExpectedMaterial",
      "QualityControl",
      "DeliveriesToSchedule",
      "DeliveriesToUnload",
      "PickupsToSchedule",
      "InstallsToSchedule",
      "UpcomingInstalls",
      "ItemsAtTemporaryDestination",
      "OpenServiceIssues",
      "ServicesToSchedule",
      "ServiceIssuePipeline",
      "UrgentServiceHotList",
    ],
  },
  management: {
    super: true,
    label: "Management",
    icon: "management",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
    ],
  },
  accounting: {
    label: "Accounting",
    icon: "accounting",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "BillsToPay",
      "PendingDates",
      "AwaitingAck",
      "AwaitingBill",
      "AwaitingDownPayment",
      "AwaitingPayment",
      "ScheduledUnpaidCashSales",
      "CashSaleEventsReminder",
    ],
  },
  delivery: {
    label: "Delivery",
    icon: "delivery",
    color: "delivery",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "DeliveriesToUnload",
    ],
  },
  estimator: {
    label: "Estimator",
    icon: "quotes",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
    ],
  },
  installation: {
    label: "Installation",
    icon: "installation",
    color: "installation",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "UpcomingInstalls",
    ],
  },
  payroll: {
    label: "Payroll",
    icon: "payroll",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
    ],
  },
  "project-manager": {
    label: "Project Manager",
    icon: "timeline",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "UpcomingInstalls",
    ],
  },
  purchaser: {
    label: "Purchaser",
    icon: "purchaser",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "PurchaserReadyToOrder",
    ],
  },
  "quality-control": {
    label: "Quality Control",
    icon: "quality_control",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "QualityControl",
    ],
  },
  sales: {
    label: "Sales",
    icon: "sales",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "ReadyToOrder",
      "PendingQuotes",
      "QuotesDue",
      "AwaitingReview",
      "OpenSalesLeads",
    ],
  },
  scheduling: {
    label: "Scheduling",
    icon: "scheduling",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "ScheduledUnpaidCashSales",
      "CashSaleEventsReminder",
      "DeliveriesToSchedule",
      "PickupsToSchedule",
      "InstallsToSchedule",
      "UpcomingInstalls",
      "ItemsAtTemporaryDestination",
    ],
  },
  service: {
    label: "Service",
    icon: "service",
    color: "service",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "OpenServiceIssues",
      "ServicesToSchedule",
      "ServiceIssuePipeline",
      "UrgentServiceHotList",
    ],
  },
  warehouse: {
    label: "Warehouse",
    icon: "warehouse",
    color: "",
    widgets: [
      "AgendaToday",
      "AgendaTomorrow",
      "OpenTasks",
      "MissedEventsReminder",
      "ExpectedMaterial",
      "DeliveriesToUnload",
    ],
  },
};
deepFreeze(USER_ROLES);

export default class User extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, USER_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.disabled = timestampToDate(data.disabled);
    data.modified = timestampToDate(data.modified);
    data.commissionEffectiveDate = timestampToDate(
      data.commissionEffectiveDate
    );

    // Convert commission schedule history dates
    if (
      data.commissionScheduleHistory &&
      data.commissionScheduleHistory.length
    ) {
      data.commissionScheduleHistory = data.commissionScheduleHistory.map(
        (schedule) => ({
          ...schedule,
          effectiveDate: timestampToDate(schedule.effectiveDate),
          endDate: timestampToDate(schedule.endDate),
          changedAt: timestampToDate(schedule.changedAt),
        })
      );
    }

    // Backward compatibility
    if (!isArray(data.position)) {
      data.position = data.position ? data.position.split(", ") : [];
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: User.canBeViewedBy,
      create: User.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      disabled: User.canBeDisabledBy,
    });
  }

  // Permissions

  get isDisabled() {
    return Boolean(this.data.disabled);
  }

  get isAdmin() {
    return this.data.roles.includes("admin");
  }

  get isSuper() {
    return this.roles.some((role) => role.super);
  }

  get isEmployee() {
    return !this.isCustomer;
  }

  get isCustomer() {
    return this.data.roles.includes("customer");
  }

  hasRole(role) {
    return this.data.roles.includes(role.toLowerCase());
  }

  static canBeViewedBy(user) {
    return user.isSuper || user.hasRole("accounting");
  }

  static canBeCreatedBy(user) {
    return user.isSuper;
  }

  canBeEditedBy(user) {
    return user.isSuper || this.id === user.id;
  }

  canBeDeletedBy() {
    return false;
  }

  static canBeDisabledBy(user) {
    return user.isSuper;
  }

  // Database

  get path() {
    return User.getPath(this.id);
  }

  static getPath(id) {
    let path = `users`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new User(snapshot.id, data);
  }

  // Editing

  get otherPOIDs() {
    return compact(
      Object.values(store.state.main.users).map((user) =>
        this.id === user.id ? "" : user.poid
      )
    );
  }

  get rules() {
    const required = (a) => Boolean(a && a.trim().length);
    const notAllCaps = (a) => !/^[A-Z]+\s+[A-Z]+$/.test(a) || "notAllCaps";
    return {
      name: [(a) => required(a) || "required", notAllCaps],
      emails: [
        () =>
          (this.data.emails.length > 0 &&
            this.emails.every((email) => email.isValid)) ||
          "required",
      ],
      poid: [
        (v) =>
          !this.isEmployee ||
          (v > 0 && v <= 999 && !this.otherPOIDs.includes(v)) ||
          "poidInvalid",
      ],
      pojob: [
        (v) => !this.isEmployee || v > 0 || "required",
        (v) => v <= 99999 || "pojobRange",
      ],
      drawAmount: [
        (v) => !this.isEmployee || v === null || v > 0 || "drawAmountPositive",
      ],
      commissionType: [
        (v) =>
          !this.isEmployee ||
          !v ||
          ["sliding", "flat", "none"].includes(v) ||
          "commissionTypeInvalid",
      ],
      commissionFlatRate: [
        (v) =>
          this.data.commissionType !== "flat" ||
          (v > 0 && v <= 100) ||
          "commissionFlatRateRange",
      ],
      commissionMinMargin: [
        (v) =>
          this.data.commissionType !== "flat" ||
          v === null ||
          (v >= 0 && v <= 100) ||
          "commissionMinMarginRange",
      ],
      roles: [(r) => (r && r.length > 0) || "required"],
      locations: [(l) => !this.isEmployee || (l && l.length > 0) || "required"],
      type: [
        (t) => this.isEmployee || required(t) || "required",
        (t) => this.isEmployee || t !== "employee" || "typeNotEmployee",
      ],
      company: [
        (a) => this.data.type === "owner" || required(a) || "required",
        notAllCaps,
      ],
      phones: [
        () => this.phones.every((phone) => phone.isValid) || "phonesInvalid",
      ],
      addresses: [
        () =>
          this.addresses.every((address) => address.isValid) ||
          "addressesInvalid",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(diff(omit(this.data, "poid"), omit(USER_FIELDS, "poid")))
    );
  }

  isEqual(contact) {
    return this.id === contact.id && isEmpty(diff(this.data, contact.data));
  }

  sanitize() {
    this.data.name = this.data.name.trim();
    this.data.company = this.data.company.trim();
    this.data.emails.forEach((email) => (email.address = email.address.trim()));
    delete this.data.notes;
    if (this.isEmployee) {
      this.data.type = "employee";
    } else {
      this.data.poid = 0;
      this.data.pojob = 1;
      this.data.locations = [];
      this.data.roles = ["customer"];
      this.data.hired = null;
      this.data.drawAmount = null;
    }
    return this;
  }

  // Formatting

  get contact() {
    return this.id ? store.state.main.contacts[this.id] || null : null;
  }

  get company() {
    return this.data.companyID
      ? store.state.main.companies[this.data.companyID] || null
      : this.data.company;
  }

  get position() {
    return this.data.position ? this.data.position.join(", ") : "";
  }

  get type() {
    return CONTACT_TYPES[this.data.type] || CONTACT_TYPES.other;
  }

  get icon() {
    return this.data.type === "employee" ? this.roles[0].icon : this.type.icon;
  }

  get emails() {
    return this.data.emails.map((email) => new Email(email));
  }

  get phones() {
    return this.data.phones.map((phone) => new Phone(phone));
  }

  get addresses() {
    return this.data.addresses.map((address) => new Address(address));
  }

  get locations() {
    return this.data.locations.map((location) => LOCATION_INFO[location]);
  }

  get roles() {
    return this.data.roles.map((role) => {
      role = USER_ROLES[role];
      return role
        ? {
            ...role,
            ...COLORS[role.color],
          }
        : null;
    });
  }

  get defaultWidgets() {
    return uniq(flatten(this.roles.map((role) => role.widgets)));
  }

  get drawAmount() {
    return this.data.drawAmount ? formatCurrency(this.data.drawAmount) : null;
  }

  get widgets() {
    // Use custom dashboard preferences if set, otherwise use role defaults
    if (
      this.data.preferences &&
      this.data.preferences.dashboardWidgets &&
      Array.isArray(this.data.preferences.dashboardWidgets)
    ) {
      return this.data.preferences.dashboardWidgets;
    }
    return this.defaultWidgets;
  }

  get balance() {
    const balance = store.state.main.balances[this.id];
    return balance ? balance.total : null;
  }

  get sentMessages() {
    return sortBy(
      Object.values(store.state.main.messages).filter(
        (c) => c.data.createdBy === this.id
      ),
      "created"
    );
  }

  get receivedMessages() {
    return sortBy(
      Object.values(store.state.main.messages).filter((m) =>
        m.data.to.includes(this.id)
      ),
      "created"
    );
  }

  get messages() {
    return sortBy(
      Object.values(store.state.main.messages).filter((c) =>
        [c.data.createdBy, ...c.data.to].includes(this.id)
      ),
      "created"
    );
  }

  get unseenMessageCount() {
    return Object.values(store.state.main.messages).filter(
      (m) => m.data.to.includes(this.id) && !m.wasSeen
    ).length;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString() {
    return this.data.name;
  }
}
